import { useCallback, useEffect, useState } from 'react'

import { helpers, useFetch } from '@reveni-io/reveni-ui'

import { statuses } from '@/returns/constants'
import returnsServices from '@/returns/services/services'

import { getLineItemStatus } from './helpers'

const useLineItems = ({ returnId, acceptAll, rejectAll, forReviewAll }) => {
  const [lineItems, setLineItems] = useState([])
  const [newRequestedProducts, setNewRequestedProducts] = useState([])
  const { data, loading, setData, resetData } = useFetch(
    () => returnsServices.getReturnDetail({ returnId: returnId }),
    {
      doInitialRequest: true,
    }
  )

  useEffect(() => {
    if (data && lineItems.length === 0) {
      setLineItems(
        helpers.joinLineItemsAndLineItemGroups(data.line_items, data?.line_item_groups).map(item => {
          if (item.status === statuses.inProgress || item.status === statuses.forReview) {
            const newStatus = getLineItemStatus({ item, acceptAll, rejectAll, forReviewAll })

            return {
              ...item,
              newStatus,
              bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, newStatus })),
            }
          }

          return item
        })
      )
    }
  }, [acceptAll, data, forReviewAll, rejectAll, lineItems?.length])

  useEffect(() => {
    if (data && newRequestedProducts.length === 0) {
      const exchangeRequestedProducts = data.line_items
        .filter(item => item.exchanged_for_item)
        .map(item => item.exchanged_for_item)

      const newProducts = data?.new_items || []
      setNewRequestedProducts([...exchangeRequestedProducts, ...newProducts])
    }
  }, [data, newRequestedProducts?.length])

  const onChangeItemStatus = (lineItemId, status) => {
    const updatedLineItems = lineItems.map(item => {
      if (item.id === lineItemId) {
        const newStatus = status === item.newStatus ? statuses.inProgress : status
        return { ...item, newStatus, bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, newStatus })) }
      }
      return item
    })

    setLineItems(updatedLineItems)
  }

  const onChangeItemReason = (lineItemId, reason) => {
    const updatedLineItems = lineItems.map(item => {
      if (item.id === lineItemId) {
        if (item.newStatus === statuses.rejected) {
          return {
            ...item,
            reject_reason: reason,
            bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, reject_reason: reason })),
          }
        } else if (item.newStatus === statuses.forReview) {
          return {
            ...item,
            for_review_reason: reason,
            bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, for_review_reason: reason })),
          }
        }
      }
      return item
    })

    setLineItems(updatedLineItems)
  }

  const onChangeItemNotes = (lineItemId, notes) => {
    const updatedLineItems = lineItems.map(item => {
      if (item.id === lineItemId) {
        if (item.newStatus === statuses.rejected) {
          return {
            ...item,
            reject_reason_text: notes,
            bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, reject_reason_text: notes })),
          }
        } else if (item.newStatus === statuses.forReview) {
          return {
            ...item,
            for_review_reason_text: notes,
            bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, for_review_reason_text: notes })),
          }
        }
      }
      return item
    })

    setLineItems(updatedLineItems)
  }

  const getNewLineItemsToAccept = () => {
    return lineItems?.filter(item => item.newStatus === statuses.accepted)
  }

  const getNewLineItemsToReject = useCallback(() => {
    return lineItems.filter(item => item.newStatus === statuses.rejected)
  }, [lineItems])

  const hasPendingItems = () => {
    return lineItems.some(item => {
      if (item.newStatus) {
        return [statuses.inProgress, statuses.forReview].includes(item.newStatus)
      }

      return [statuses.inProgress, statuses.forReview].includes(item.status)
    })
  }

  const hasAcceptedItems = () => {
    return lineItems.some(item => {
      if (item.newStatus) {
        return [statuses.accepted].includes(item.newStatus)
      }

      return [statuses.accepted].includes(item.status)
    })
  }

  const uploadProductImage = async imageFile => {
    const formData = new FormData()
    formData.append('image', imageFile)

    const response = await returnsServices.uploadProductImage({ resourceId: returnId, formData })

    return { id: response?.data?.id, url: response?.data.url }
  }

  const onChangeItemImages = async (lineItemId, imageFiles) => {
    await imageFiles.forEach(async imageFile => {
      const response = await uploadProductImage(imageFile)

      setLineItems(updatedLineItems =>
        updatedLineItems.map(item => {
          if (item.id === lineItemId) {
            const images = item.images
              ? [...item.images, { ...response, status: 'new' }]
              : [{ ...response, status: 'new' }]
            return {
              ...item,
              images,
              bundleItems: item?.bundleItems?.map(bundleItem => ({
                ...bundleItem,
                images,
              })),
            }
          }
          return item
        })
      )
    })
  }

  const onDeleteItemImage = (lineItemId, imageId) => {
    setLineItems(updatedLineItems =>
      updatedLineItems.map(item => {
        if (item.id === lineItemId) {
          const images = item.images.filter(image => image.id !== imageId)
          return { ...item, images, bundleItems: item?.bundleItems?.map(bundleItem => ({ ...bundleItem, images })) }
        }
        return item
      })
    )
  }

  const hasSomeItemDelayedStatus = () => {
    return lineItems.some(item => item.newStatus === statuses.rejected && item.reject_reason === 'delay')
  }

  return {
    lineItems,
    loading,
    transaction: data,
    onChangeItemStatus,
    onChangeItemReason,
    onChangeItemNotes,
    onChangeItemImages,
    onDeleteItemImage,
    numberOfNewAcceptedItems: getNewLineItemsToAccept()?.length || 0,
    numberOfNewRejectedItems: getNewLineItemsToReject()?.length || 0,
    numberOfNewRequestedProducts: newRequestedProducts?.length || 0,
    getNewLineItemsToReject,
    getNewLineItemsToAccept,
    uploadProductImage,
    hasPendingItems,
    hasAcceptedItems,
    hasSomeItemDelayedStatus,
    changeOriginalTransaction: setData,
    resetTransaction: resetData,
  }
}

export default useLineItems
